/**
 * HTTP client for the home Qwen3-TTS server.
 *
 * Server: https://cosy.yarder.app/v1/audio/speech (OpenAI-compatible).
 * Voices: alloy → Vivian (English), nova → Sohee (Spanish).
 *
 * Returns SynthesisOutput so callers can play via the shared PCM playback
 * pipeline. (Defined here now that the on-device browserTTS path was removed.)
 */
export interface SynthesisOutput {
  audio: Float32Array;
  sampleRate: number;
  numChannels: number;
  numFrames: number;
}

export type TtsLanguage = 'English' | 'Spanish';

const SERVER_URL = 'https://cosy.yarder.app';
// 60s covers up to ~50s of generated audio at RTF ~1.2. AI replies that
// long are unusual but happen when Gemini ignores its own "2-3 sentences"
// instruction and produces a paragraph. The caller's AbortSignal is the
// real interrupt mechanism — this is just a watchdog against a stuck
// connection.
const REQUEST_TIMEOUT_MS = 60_000;

const VOICE_FOR_LANGUAGE: Record<TtsLanguage, string> = {
  English: 'alloy',  // → Vivian
  Spanish: 'nova',   // → Sohee
};

const MODEL_FOR_LANGUAGE: Record<TtsLanguage, string> = {
  English: 'tts-1-en',
  Spanish: 'tts-1-es',
};

export class ServerTtsError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = 'ServerTtsError';
  }
}

/**
 * Synthesize via the home server. Returns the raw WAV bytes AND a decoded
 * SynthesisOutput. The Blob is what we cache in IndexedDB; the SynthesisOutput
 * is what playback wants.
 */
export async function synthesizeViaServer(
  text: string,
  language: TtsLanguage,
  signal?: AbortSignal,
): Promise<{ wav: Blob; output: SynthesisOutput }> {
  const ac = new AbortController();
  const timeoutId = setTimeout(() => ac.abort('timeout'), REQUEST_TIMEOUT_MS);
  if (signal) {
    if (signal.aborted) ac.abort(signal.reason);
    else signal.addEventListener('abort', () => ac.abort(signal.reason), { once: true });
  }

  try {
    const res = await fetch(`${SERVER_URL}/v1/audio/speech`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        input: text,
        voice: VOICE_FOR_LANGUAGE[language],
        model: MODEL_FOR_LANGUAGE[language],
        response_format: 'wav',
      }),
      signal: ac.signal,
    });

    if (!res.ok) {
      throw new ServerTtsError(`server ${res.status}: ${await res.text().catch(() => '')}`, res.status);
    }

    const wav = await res.blob();
    const output = await decodeWavBlob(wav);
    return { wav, output };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Stream raw PCM chunks from the server as they arrive. Yields Float32Arrays
 * (mono, 24kHz) suitable for direct playback via AudioBufferSourceNode.
 *
 * This is the fast path — TTFB on the server is ~750ms (vs ~4-5s for full
 * WAV), so the user starts hearing audio under a second instead of after
 * the entire reply is generated.
 *
 * Server returns 16-bit signed little-endian PCM with chunked transfer
 * encoding. We buffer up odd byte counts across chunk boundaries so a
 * 16-bit sample never gets split.
 */
export async function* streamPcmFromServer(
  text: string,
  language: TtsLanguage,
  signal?: AbortSignal,
): AsyncGenerator<Float32Array, void, void> {
  const res = await fetch(`${SERVER_URL}/v1/audio/speech`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      input: text,
      voice: VOICE_FOR_LANGUAGE[language],
      model: MODEL_FOR_LANGUAGE[language],
      response_format: 'pcm',
      stream: true,
    }),
    signal,
  });

  if (!res.ok) {
    throw new ServerTtsError(`server ${res.status}: ${await res.text().catch(() => '')}`, res.status);
  }
  if (!res.body) {
    throw new ServerTtsError('server returned no body', res.status);
  }

  const reader = res.body.getReader();
  let leftover: Uint8Array | null = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value || value.length === 0) continue;

      // Concat any odd-byte leftover from the previous chunk
      let bytes = value;
      if (leftover && leftover.length > 0) {
        const merged = new Uint8Array(leftover.length + value.length);
        merged.set(leftover);
        merged.set(value, leftover.length);
        bytes = merged;
        leftover = null;
      }

      // 16-bit samples → must process even byte count
      const usableLen = bytes.length - (bytes.length % 2);
      if (usableLen === 0) {
        leftover = bytes;
        continue;
      }
      if (usableLen < bytes.length) {
        leftover = bytes.subarray(usableLen);
      }

      // Make a copy so the underlying buffer is properly aligned for Int16Array
      const aligned = new Uint8Array(bytes.subarray(0, usableLen));
      const int16 = new Int16Array(aligned.buffer);
      const float32 = new Float32Array(int16.length);
      for (let i = 0; i < int16.length; i++) {
        float32[i] = int16[i] / 0x8000;
      }
      yield float32;
    }
  } finally {
    try { await reader.cancel(); } catch { /* ignore */ }
  }
}

/**
 * Sample rate of the streaming PCM output. Qwen3-TTS-12Hz emits at 24 kHz.
 * Hardcoded — server doesn't currently expose this in headers.
 */
export const STREAM_SAMPLE_RATE = 24000;

/**
 * Decode a WAV blob into the SynthesisOutput shape (Float32Array PCM,
 * sample rate, channel count). Uses the shared 24 kHz streaming AudioContext
 * for decoding so we don't proliferate contexts and so the decoded buffer's
 * sample rate matches the playback context exactly (no resampling).
 */
export async function decodeWavBlob(wav: Blob): Promise<SynthesisOutput> {
  // Imported lazily to break a circular import (streamPlayer → server → streamPlayer).
  const { getStreamingAudioContext } = await import('./streamPlayer');
  const arrayBuffer = await wav.arrayBuffer();
  const ctx = getStreamingAudioContext();
  const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
  const numChannels = audioBuffer.numberOfChannels;
  const numFrames = audioBuffer.length;
  const interleaved = new Float32Array(numFrames * numChannels);
  for (let ch = 0; ch < numChannels; ch++) {
    const channelData = audioBuffer.getChannelData(ch);
    for (let s = 0; s < numFrames; s++) {
      interleaved[s * numChannels + ch] = channelData[s];
    }
  }
  return {
    audio: interleaved,
    sampleRate: audioBuffer.sampleRate,
    numChannels,
    numFrames,
  };
}

/**
 * Detect language from text content. Heuristic-based:
 *   1. Strong Spanish-only signals (ñ, ¿, ¡, accented vowels) ⇒ Spanish.
 *   2. Otherwise, count Spanish stop-words and characteristically-Spanish
 *      tokens ("hola", "gracias", number names like "mil", "ciento") vs
 *      English equivalents. Spanish needs to beat English by 1+ to win.
 *   3. Tie or no signal ⇒ English (Qwen3-TTS handles English-spoken-with-
 *      Spanish-words better than Spanish-spoken-with-English-words).
 *
 * Why we need this: Gemini follows BASE_INSTRUCTIONS to spell out numbers
 * and avoid markdown — many Spanish replies are short ("Listo.", "Sí.",
 * "Por supuesto.") with no accent chars, so the original detector
 * defaulted them to English and Vivian tried to pronounce them.
 */
export function detectLanguage(text: string): TtsLanguage {
  const t = text.toLowerCase();

  // Strong Spanish-only signals — single match wins immediately.
  if (/[ñ¿¡]/.test(text)) return 'Spanish';
  if (/[áéíóúü]/.test(t)) return 'Spanish';

  // Word-level stop-word + content-word counts
  const SP_WORDS = [
    // articles + prepositions
    'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas',
    'de', 'del', 'al', 'en', 'para', 'por', 'con', 'sin',
    // pronouns + common verbs
    'que', 'lo', 'mi', 'tu', 'su', 'es', 'son', 'tu', 'me', 'te', 'se',
    'está', 'esta', 'este', 'estos', 'eso', 'esto',
    // very common conversational words
    'hola', 'gracias', 'sí', 'si', 'no', 'pero', 'también', 'tambien',
    'claro', 'listo', 'bien', 'bueno', 'buenos', 'buenas', 'días', 'dias',
    'ayudar', 'ayuda', 'puede', 'puedo', 'tengo', 'tiene',
    // CRM-domain Spanish content words
    'cliente', 'clientes', 'presupuesto', 'presupuestos', 'proyecto', 'proyectos',
    'estimado', 'estimados', 'tarifa', 'precio', 'cita', 'citas', 'hoy', 'mañana',
    // number names that come from spell-out
    'mil', 'millón', 'millones', 'ciento', 'cientos', 'cien', 'dólares', 'dolares',
    'centavos', 'por', 'ciento',
  ];
  const EN_WORDS = [
    'the', 'a', 'an', 'and', 'or', 'is', 'are', 'was', 'were', 'be', 'been',
    'of', 'to', 'in', 'on', 'at', 'for', 'with', 'from', 'as',
    'you', 'your', 'i', 'my', 'we', 'our', 'they', 'them',
    'this', 'that', 'these', 'those', 'will', 'would', 'have', 'has',
    'good', 'morning', 'afternoon', 'evening', 'thanks', 'please', 'sure',
    'client', 'clients', 'estimate', 'estimates', 'project', 'projects',
    'price', 'rate', 'today', 'tomorrow', 'thousand', 'hundred', 'dollars', 'cents',
  ];

  let sp = 0;
  let en = 0;
  for (const w of SP_WORDS) if (new RegExp(`\\b${w}\\b`, 'i').test(t)) sp++;
  for (const w of EN_WORDS) if (new RegExp(`\\b${w}\\b`, 'i').test(t)) en++;

  // Require Spanish to beat English by at least 1 to flip from default.
  return sp > en ? 'Spanish' : 'English';
}
